import {
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
  FocusEvent,
} from "react"
import { useI18n } from "providers/I18n"
import { styles } from "./styles"
import { TextInput } from "components/inputs/TextInput"
import { Company } from "models/company"
import get from "lodash/fp/get"
import assoc from "lodash/fp/set"
import { queryCache, useMutation, useQuery } from "react-query"
import { patchCompany } from "api/companies"
import { ApiError, ErrorsMap } from "api/http/utils"
import AutoCompleteInput from "components/inputs/AutoComplete"
import { fetchCountries } from "api/geography"
import { useAuthenticated } from "providers/A12n"
import { Loading } from "components/Loading"

type Props = {
  data?: Company
  loading?: boolean
}

type CountryOption = { name: string; code: string }

const InitialState = {
  id: "",
  name: "",
  code: "",
  sstRn: "",
}

function Profile(props: Props) {
  const { t } = useI18n()
  const { data, loading } = props
  const [state, setState] = useState<Company>(() => InitialState)
  const [errors, setErrors] = useState<ErrorsMap | undefined>()
  const { hasAuthority } = useAuthenticated()

  const [patch] = useMutation(patchCompany, {
    onSuccess: () => {
      queryCache.invalidateQueries("company")
      queryCache.invalidateQueries("companies")
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handleChange = (
    evt: ChangeEvent<
      | HTMLInputElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
  ) => {
    const { name, value } = evt?.target
    !!name && setState((s) => assoc(name, value, s))
  }

  const handleBlur = useCallback(
    (section?: "address") =>
      (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name = "", value = "" } = evt?.target ?? {}
        !!data &&
          value !== get(name, data) &&
          patch({
            id: data?.id,
            [section || name]: state[section || (name as keyof Company)],
          })
      },
    [patch, data, state],
  )

  const handleCountryChange = useCallback(
    (countryCode?: string) => {
      !!countryCode &&
        patch({ id: data?.id, address: { ...state.address, countryCode } })
    },
    [patch, data, state],
  )

  useEffect(() => {
    if (!!data) {
      setState(() => data)
    }
  }, [data])

  const { data: countries } = useQuery(["countries"], fetchCountries, {
    staleTime: 120000,
  })

  const countrySelected = countries?.data.find(
    (country) => country.code === state?.address?.countryCode,
  ) || { name: "", code: "" }

  return loading ? (
    <Loading />
  ) : (
    <div className={styles.main}>
      <section className={styles.section}>
        <span className={styles.sectionTitle}>
          {t("company.profile.details")}
        </span>
        <div className={styles.input}>
          <TextInput
            name="name"
            value={data?.localName || ""}
            label={t("company.list.localName")}
            margin="normal"
            size="medium"
            className={styles.fieldWidth}
            disabled={true}
          />
          <TextInput
            name="code"
            value={data?.code || ""}
            label={t("company.list.code")}
            margin="normal"
            size="medium"
            className={styles.fieldWidth}
            disabled={true}
          />
          {!!data?.roc ? (
            <TextInput
              name="roc"
              value={data?.roc || ""}
              label={t("company.list.roc")}
              margin="normal"
              size="medium"
              className={styles.fieldWidth}
              disabled={true}
            />
          ) : null}
        </div>
        <div className={styles.input}>
          <TextInput
            name="market"
            value={data?.marketName || ""}
            label={t("company.list.market")}
            margin="normal"
            size="medium"
            className={styles.fieldWidth}
            disabled={true}
          />
          <TextInput
            name="subRegionName"
            value={data?.subRegionName || ""}
            label={t("company.list.subRegion")}
            margin="normal"
            size="medium"
            className={styles.fieldWidth}
            disabled={true}
          />
          <TextInput
            name="region"
            value={data?.regionName || ""}
            label={t("company.list.region")}
            margin="normal"
            size="medium"
            className={styles.fieldWidth}
            disabled={true}
          />
          <TextInput
            name="profitCenter"
            value={data?.profitCenter || ""}
            label={t("company.list.profitCenter")}
            margin="normal"
            size="medium"
            className={styles.fieldWidth}
            disabled={true}
          />
          <TextInput
            name="costCenter"
            value={data?.costCenter || ""}
            label={t("company.list.costCenter")}
            margin="normal"
            size="medium"
            className={styles.fieldWidth}
            disabled={true}
          />
          <TextInput
            name="sstRn"
            value={state?.sstRn || ""}
            label={t("company.list.sstRn")}
            onChange={handleChange}
            onBlur={handleBlur()}
            margin="normal"
            size="medium"
            className={styles.fieldWidth}
            disabled={!hasAuthority(["AUTH_EDIT_COMPANY"])}
          />
          <TextInput
            fullWidth
            rows={1}
            rowsMax={4}
            autoFocus={true}
            name="paymentDetails"
            value={state?.paymentDetails || ""}
            label={t("company.list.paymentDetails")}
            placeholder={"Add Payment Details"}
            onChange={handleChange}
            onBlur={handleBlur()}
            margin="normal"
            size="medium"
            className={styles.fieldWidth}
            multiline
            disabled={!hasAuthority(["AUTH_EDIT_COMPANY"])}
          />
        </div>
      </section>
      <section className={styles.section}>
        <span className={styles.sectionTitle}>
          {t("company.profile.addresses")}
        </span>
        <div className={styles.addresses}>
          {t("company.profile.business-address")}
        </div>
        <div className={styles.inputsWrapper}>
          <div className={styles.address}>
            <div>
              <TextInput
                name="address.address1"
                value={state?.address?.address1 || ""}
                onChange={handleChange}
                onBlur={handleBlur("address")}
                className={styles.fieldWidthWider}
                label={t("company.profile.address1")}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_COMPANY"])}
              />
            </div>
            <div>
              <TextInput
                name="address.address2"
                value={state?.address?.address2 || ""}
                onChange={handleChange}
                onBlur={handleBlur("address")}
                className={styles.fieldWidthWider}
                label={t("company.profile.address2")}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_COMPANY"])}
              />
            </div>
            <div className={styles.dropdownInput}>
              <AutoCompleteInput<CountryOption>
                label={t("supplier.profile.country")}
                size="medium"
                margin="normal"
                className={styles.field}
                options={countries?.data}
                onChange={(val) => {
                  handleCountryChange(val?.code)
                }}
                getOptionSelected={(option, value) => {
                  return option.code === value.code
                }}
                renderOption={(option) => (
                  <span>
                    {option.name} ({option.code})
                  </span>
                )}
                getOptionLabel={(option) =>
                  option.name && `${option.name} (${option.code})`
                }
                value={countrySelected || ""}
                fetchOnFilter={false}
                error={Boolean(errors?.countryCode)}
                helperText={errors?.countryCode}
                disabled={!hasAuthority(["AUTH_EDIT_COMPANY"])}
              />
              <TextInput
                name="address.state"
                value={state?.address?.state || ""}
                onChange={handleChange}
                onBlur={handleBlur("address")}
                label={t("company.profile.state")}
                className={styles.field}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_COMPANY"])}
              />
              <TextInput
                name="address.city"
                value={state?.address?.city || ""}
                onChange={handleChange}
                onBlur={handleBlur("address")}
                label={t("company.profile.city")}
                className={styles.field}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_COMPANY"])}
              />
              <TextInput
                name="address.zipCode"
                value={state?.address?.zipCode || ""}
                onChange={handleChange}
                onBlur={handleBlur("address")}
                label={t("company.profile.zipCode")}
                className={styles.zip}
                margin="normal"
                size="medium"
                disabled={!hasAuthority(["AUTH_EDIT_COMPANY"])}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Profile
